import { parseSecretManagerField } from "@p0security/integrations-vaulted-credential-shared";
import { assertNever } from "@p0security/util/assertNever";

import type { Auth0TenantConfig } from "./types.ts";

/** Where a tenant's client secret is, in the customer's own secret manager:
 * the pointer P0 gives the connector on every call, as the connector's
 * `clientSecret` schema (p0-connector-auth0) takes it. P0 never holds the
 * secret. */
export type ClientSecret =
  | { manager: "aws"; region: string; secretId: string }
  | { manager: "gcp"; projectId: string; secretId: string };

/**
 * The partial ARN of the AWS Secrets Manager secret `name`, in `accountId` and
 * `region`: the secret's ARN without the hyphen and six random characters that
 * Secrets Manager adds to its name.
 *
 * Secrets Manager finds a secret by its partial ARN from another account too,
 * which a bare name doesn't allow. The tenant's Terraform grants read on
 * it followed by `-??????`. It trims each value, as P0 stores it. P0 takes
 * only regions in AWS's commercial partition, so the partition is `aws`.
 *
 * It copies github-repositories' helper instead of sharing it, so adding
 * Auth0 changes no other integration.
 */
const toSecretPartialArn = ({
  accountId,
  name,
  region,
}: {
  accountId: string;
  name: string;
  region: string;
}) =>
  `arn:aws:secretsmanager:${region.trim()}:${accountId.trim()}:secret:${name.trim()}`;

/**
 * Points the connector at the secret that holds a tenant's client secret: by
 * its partial ARN on AWS, and by its name and project on Google
 * Cloud.
 *
 * It trims each value, as P0 stores it, because install checks see the item
 * as the request left it.
 */
export const toClientSecret = ({
  clientSecretName,
  vault,
}: Pick<Auth0TenantConfig, "clientSecretName" | "vault">): ClientSecret => {
  const locator = parseSecretManagerField(vault);
  const name = clientSecretName.trim();
  switch (locator.provider) {
    case "aws":
      return {
        manager: "aws",
        region: locator.region.trim(),
        secretId: toSecretPartialArn({
          accountId: locator.accountId,
          name,
          region: locator.region,
        }),
      };
    case "gcp":
      return {
        manager: "gcp",
        projectId: locator.projectId.trim(),
        secretId: name,
      };
    default:
      throw assertNever(locator);
  }
};
